import {makeAutoObservable, runInAction} from 'mobx'
import config from 'src/config'
import {MainStore} from 'src/store/MainStore'
import MonthCalcSetting from 'src/entities/MonthCalcSetting'
import InstitutionMonthlyReport from 'src/entities/InstitutionMonthlyReport'
import StipendPaymentStatus from 'src/entities/StipendPaymentStatus'
import {getLocalBaseDateString} from 'src/utils/date'

export class PaymentSettingsStore {
  currentSetting: MonthCalcSetting | null = null

  monthlyReports: InstitutionMonthlyReport[] = []

  stipendStatuses: StipendPaymentStatus[] = []

  isLoading: boolean = false

  constructor(readonly owner: MainStore) {
    makeAutoObservable(this)
  }

  loadResolvedSetting = async (month: number, year: number) => {
    this.isLoading = true
    try {
      const res = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/MonthCalcSettings/resolve/${month}/${year}`
      )
      const data = await res.json()
      runInAction(() => {
        this.currentSetting = data ? new MonthCalcSetting(data) : null
        this.isLoading = false
      })
    } catch (e) {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  upsertMonthCalcSetting = async (setting: MonthCalcSetting) => {
    const res = await this.owner.loginStore.fetchWithUser(
      `${config.apiUrl}/MonthCalcSettings`,
      {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          Id: setting.id,
          Month: setting.month,
          NumericYear: setting.numericYear,
          PaymentPercent: setting.paymentPercent,
          PaymentCapDollar: setting.paymentCapDollar,
          DollarRate: setting.dollarRate
        })
      }
    )
    if (res.status === 409) {
      this.owner.setMessage('לא ניתן לעדכן הגדרות לחודש ששולם.')
      throw new Error('locked')
    }
    const data = await res.json()
    const newSetting = new MonthCalcSetting(data)
    runInAction(() => {
      this.currentSetting = newSetting
    })
    return newSetting
  }

  loadMonthlyReports = async (institutionId: number) => {
    const res = await this.owner.loginStore.fetchWithUser(
      `${config.apiUrl}/MonthlyReports/institution/${institutionId}`
    )
    const data = await res.json()
    runInAction(() => {
      this.monthlyReports = (data || []).map(
        (d: any) => new InstitutionMonthlyReport(d)
      )
    })
  }

  loadStipendStatuses = async (institutionId: number) => {
    this.isLoading = true
    try {
      const res = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/MonthlyReports/StipendStatus/${institutionId}`
      )
      const data = await res.json()
      runInAction(() => {
        this.stipendStatuses = (data || []).map(
          (d: any) => new StipendPaymentStatus(d)
        )
        this.isLoading = false
      })
    } catch {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  upsertMonthlyReport = async (report: InstitutionMonthlyReport) => {
    const body = {
      Id: Number(report.id) || 0,
      InstitutionId: Number(report.institutionId),
      Month: Number(report.month),
      NumericYear: Number(report.numericYear),
      ActualStipendPaidDate: getLocalBaseDateString(report.actualStipendPaidDate)
    }
    let res: Response
    try {
      res = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/MonthlyReports`,
        {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(body)
        }
      )
    } catch (err) {
      if (err instanceof Response) {
        const text = await err.text().catch(() => '')
        console.error('upsertMonthlyReport failed', err.status, text, body)
        throw new Error(`upsertMonthlyReport ${err.status}: ${text}`)
      }
      throw err
    }
    const data = await res.json()
    return new InstitutionMonthlyReport(data)
  }
}
